import React from 'react';
import styles from '../css/modules/ContentPage.module.css';
import SEO from '../components/SEO';
import JsonLd from './JsonLd';
import Breadcrumbs from './Breadcrumbs';
import { FIRMA } from '../data/firma';
import { serviceSchema, breadcrumbSchema } from '../data/schema';

const PATH = '/terapia-reki/';

const CRUMBS = [
  { name: 'Strona główna', path: '/' },
  { name: 'Terapia ręki', path: PATH },
];

export default function TerapiaReki() {
  // Usluga odsyla do jednego wezla firmy (@id "#business") zamiast opisywac
  // wlasnego providera z powielonym adresem i telefonem (spec sekcja 12).
  const jsonLd = serviceSchema({
    path: PATH,
    name: 'Terapia ręki dla dzieci w Olsztynie',
    serviceType: 'Terapia ręki',
  });

  return (
    <div className={styles.page}>
      <SEO
        title="Terapia ręki dla dzieci w Olsztynie - Sensorini Olsztyn"
        description="Terapia ręki dla dzieci w Olsztynie. Ćwiczenia małej motoryki, grafomotoryki, koordynacji wzrokowo-ruchowej i napięcia mięśniowego. Zajęcia terapeutyczne dla dzieci z Olsztyna i okolic."
        pathname="/terapia-reki/"
        type="article"
      />

      <JsonLd data={[jsonLd, breadcrumbSchema(CRUMBS)]} />

      <header className={styles.hero}>
        <span className={`${styles.blob} ${styles.blobA}`} aria-hidden="true" />
        <span className={`${styles.blob} ${styles.blobB}`} aria-hidden="true" />
        <div className={styles.heroInner}>
          <span className={styles.eyebrow}>Terapia ręki • Olsztyn</span>
          <h1 className={styles.h1}>Terapia ręki dla dzieci w Olsztynie</h1>
          <p className={styles.lead}>
            <strong>Terapia ręki w Olsztynie</strong> to specjalistyczne zajęcia dla dzieci, które mają trudności z
            małą motoryką, grafomotoryką, koordynacją wzrokowo-ruchową i czynnościami samoobsługowymi.
            W gabinecie Sensorini w Olsztynie pracuję z dziećmi nad sprawnością dłoni, nadgarstka i całej kończyny górnej,
            aby ułatwić im funkcjonowanie w przedszkolu, szkole i w domu.
          </p>
        </div>
      </header>

      <Breadcrumbs items={CRUMBS} />

      <div className={styles.wrap}>
        <section className={styles.section}>
          <h2>Dla kogo jest terapia ręki w Olsztynie?</h2>
          <p>
            <strong>Terapia ręki dla dzieci</strong> w moim gabinecie w Olsztynie jest przeznaczona dla dzieci, u których
            rodzice, nauczyciele lub specjaliści zauważają trudności z precyzyjnymi ruchami dłoni, pisaniem czy manipulacją
            drobnymi przedmiotami. Na terapię ręki w Olsztynie zapraszam dzieci, u których występują:
          </p>
          <ul className={styles.list}>
            <li>nieprawidłowości w napięciu mięśniowym i postawie ciała,</li>
            <li>zaburzenia integracji sensorycznej, grafomotoryki i precyzji ruchu,</li>
            <li>brak koordynacji ruchów kończyny górnej i niezgrabność ruchowa,</li>
            <li>zaburzenia koordynacji wzrokowo-ruchowej, trudności z rzucaniem i chwytaniem,</li>
            <li>opóźnienia w czynnościach samoobsługowych (ubieranie się, zapinanie guzików, wiązanie butów),</li>
            <li>
              nadwrażliwość lub obniżona wrażliwość dotykowa
              (np. niechęć do plasteliny, lekkiego dotyku, nowych ubrań,
              wkładanie przedmiotów do ust, preferencja twardych przedmiotów).
            </li>
          </ul>
          <p className={styles.callout}>Terapia ręki dla dzieci od 3. roku życia - Olsztyn</p>
          <img
            src="/image/terapia-reki.webp"
            alt="Terapia ręki dla dzieci w Olsztynie w gabinecie Sensorini"
            loading="lazy"
            className={styles.figure}
          />
        </section>

        <section className={styles.section}>
          <h2>Jakie są główne cele terapii ręki?</h2>
          <p>
            Celem <strong>terapii ręki w Olsztynie</strong> jest usprawnianie całej kończyny górnej, poprawa precyzji ruchów,
            przygotowanie ręki do pisania oraz zwiększenie samodzielności dziecka. Podczas zajęć z terapii ręki dla dzieci
            pracujemy nad:
          </p>
          <ul className={styles.list}>
            <li>dostarczaniem wrażeń dotykowych i różnicowaniem kształtów oraz faktur,</li>
            <li>usprawnianiem małej motoryki (precyzyjne ruchy dłoni i palców),</li>
            <li>wypracowaniem prawidłowego chwytu narzędzi pisarskich,</li>
            <li>rozwijaniem zdolności skupienia uwagi i patrzenia, wzmacnianiem koncentracji,</li>
            <li>poprawą koordynacji wzrokowo-ruchowej,</li>
            <li>kształtowaniem umiejętności przekraczania linii środkowej ciała.</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>Zakres pracy na terapii ręki</h2>
          <p>
            <strong>Terapia ręki dla dzieci w Olsztynie</strong> obejmuje pracę z całą kończyną górną, od obręczy barkowej aż po
            palce dłoni. Ćwiczenia dobieram indywidualnie do potrzeb dziecka, łącząc elementy terapii ręki i
            <strong> integracji sensorycznej</strong>.
          </p>
          <ul className={styles.chips}>
            <li>cała kończyna górna</li>
            <li>obręcz barkowa: obojczyk, łopatka</li>
            <li>kość ramienna - ćwiczenia rozmachowe</li>
            <li>kość łokciowa i promieniowa - ćwiczenia manualne</li>
            <li>kości ręki - ćwiczenia manipulacyjne i grafomotoryczne</li>
            <li>korekcja postawy, łopatek i ułożenia miednicy</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>Jak zacząć?</h2>
          <p>
            Jeżeli widzisz u dziecka opisane wyżej trudności, zapraszam do sali terapeutycznej w
            centrum Olsztyna ({FIRMA.ulica}). Terapia ręki może ułatwić naukę pisania i zwiększyć
            samodzielność w codziennych czynnościach - zakres i tempo pracy zależą jednak od potrzeb
            konkretnego dziecka.
          </p>
          <p>
            Jeśli trudności dotyczą nie tylko ręki, ale też równowagi, koordynacji czy reakcji na
            bodźce, warto zacząć od{' '}
            <a href="/diagnoza-sensoryczna-olsztyn/">diagnozy integracji sensorycznej</a> - pokaże,
            co dokładnie wymaga wsparcia. Ceny znajdziesz w <a href="/cennik/">cenniku</a>.
          </p>
        </section>
      </div>

      <section className={styles.ctaBand}>
        <div className={styles.ctaInner}>
          <h2>Umów terapię ręki w Olsztynie</h2>
          <p>Skontaktuj się - dobierzemy plan wspierający motorykę małą i grafomotorykę Twojego dziecka.</p>
          <div className={styles.ctaBtns}>
            <a href="/diagnoza-sensoryczna-olsztyn/" className={styles.btnPrimary} data-cta="booking" data-cta-location="final">Umów diagnozę</a>
            <a href={`tel:${FIRMA.telefonE164}`} className={styles.btnGhost}>Zadzwoń: {FIRMA.telefon}</a>
          </div>
        </div>
      </section>
    </div>
  );
}
